import { useState } from 'react';
import { DataGridPro, GridColumnVisibilityModel, GridToolbar } from '@mui/x-data-grid-pro';
import { Box, Typography } from '@mui/material';
import { alpha, Stack, useTheme } from '@mui/system';
import { useSelector } from 'react-redux';
import { formatCurrency } from 'src/utils/format-currency';
import { useLocales } from 'src/locales';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import DatagridDiffCell from './data-grid-diff-cell';
import CustomLineChart from '../inline-chart/inline-chart';
import Iconify from '../iconify';
import countries from '../../utils/countries.json';
import LinearProgress from '@mui/material/LinearProgress';
import { formatNumber } from 'src/utils/format-number';

const getCountryCode = (name: any) => countries.find((c) => c.name.toLowerCase() === name.toLowerCase())?.code;

const ColorSchema = {
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
    TERTIARY: 'tertiary',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    SUCCESS: 'success'
};

const renderProgressCell = (value: any, max: any, currency: any, locale: any, color: any, theme: any) => {
    const percent = max ? (value / max) * 100 : 0;
    return (
        <Stack direction="column" sx={{ width: 1, px: 1 }}>
            <LinearProgress
                variant="determinate"
                value={percent}
                sx={{
                    height: 6,
                    borderRadius: 5,
                    backgroundColor: alpha(theme.palette.grey[500], 0.2),
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: theme?.palette[color]?.darker,
                    }
                }}
            />
        </Stack>
    );
};

const getMaxValue = (rows: any[], key: string) => Math.max(...rows.map((row: any) => row[key] ?? 0));

const capitalizeFirst = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

const getColumns = ({ t, currentLang, workspace, theme, filterStore, split, isCompare, currency, locale, maxValues, sinceCrossBrand }: any) => {
    return [
        {
            field: 'split',
            headerName: split.charAt(0).toUpperCase() + split.slice(1).toLowerCase(),
            minWidth: 200,
            renderCell: ({ value }: any) => {
                const lowerSplit = split.toLowerCase();
                if (lowerSplit === 'platform') {
                    const display = capitalizeFirst(value);
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {display.toLowerCase() === 'google' && <Iconify icon="devicon:google" sx={{ width: 16, height: 16 }} />}
                            {display.toLowerCase() === 'bing' && <Iconify icon="logos:bing" sx={{ width: 16, height: 16 }} />}
                            {display}
                        </Box>
                    );
                }
                if (lowerSplit === 'device') {
                    // Only capitalize first letter
                    const normalized = capitalizeFirst(value);
                    const icons: any = {
                        Mobile: 'icon-park:iphone',
                        Desktop: 'icon-park:i-mac',
                        Tablet: 'icon-park:ipad',
                    };
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {icons[normalized] && <Iconify icon={icons[normalized]} sx={{ width: 16, height: 16 }} />}
                            {normalized}
                        </Box>
                    );
                }
                if (lowerSplit === 'country') {
                    const code = getCountryCode(value);
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {code ? (
                                <img
                                    src={`https://flagcdn.com/w20/${code}.png`}
                                    alt={value}
                                    style={{ width: 20, height: 15, borderRadius: 2 }}
                                />
                            ) : (
                                <img
                                    style={{ width: 14, height: 14, marginRight: 6 }}
                                    src="/assets/location.png"
                                    alt="flag"
                                />
                            )}
                            {value}
                        </Box>
                    );
                }
                return value;
            },
        },
        {
            field: 'savings',
            headerName: t('dashboard.filters.savings'),
            minWidth: 240,
            flex: 1,
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>{t('dashboard.filters.savings')}</span>
                </div>
            ),
            renderCell: (params: any) => {
                const series = [{
                    name: 'Savings',
                    data: params.row.sparkline_savings?.map((point: any) => ({
                        x: new Date(point.date).getTime(),
                        y: point.value
                    })) || []
                }];
                return (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, width: 1, height: 1 }}>
                        {split === 'date'
                            ? renderProgressCell(params.value, maxValues.savings, currency, locale, ColorSchema.SUCCESS, theme)
                            : <CustomLineChart series={series} formatValue={(value) => formatCurrency(value, currency, locale, true, 2)} color={'#36B37E'} type="bar" height={30} width={100} />
                        }
                        <Typography variant="caption" sx={{ width: 80, textAlign: "right" }}>
                            {formatCurrency(params.value, currency, locale, true, 1)}
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        ...(split !== 'date' ? [{
            field: 'savings_diff',
            headerName: 'Savings Difference',
            minWidth: 260,
            flex: 1,
            type: 'number',
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>Savings</span>
                    Difference
                </div>
            ),
            renderCell: (params: any) => {
                const value = Math.round((params.row.savings ?? 0) - (params.row.savings_compare ?? 0));
                const base = params.row.savings_compare ?? 0;
                const percent = base !== 0 ? (value / base) * 100 : 0;
                return (
                    <DatagridDiffCell
                        value={value}
                        compareChip={percent}
                        unit="currency"
                        colors={{ dark: '#b7b7b7', light: '#B7B7B7' }}
                        invertedColor
                    />
                );
            }
        }] : []),
        {
            field: 'ad_cost_with_cb',
            headerName: 'Ad Cost with Primelis Signal',
            minWidth: isCompare ? 260 : 100,
            flex: 1,
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>Ad Cost with Primelis Signal</span>
                </div>
            ),
            renderCell: (params: any) => {
                const series = [{
                    name: 'Ad Cost w/ CB',
                    data: params.row.sparkline_ad_cost_with_cb?.map((point: any) => ({
                        x: new Date(point.date).getTime(),
                        y: point.value
                    })) || []
                }];
                return (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, width: 1, height: 1 }}>
                        {split === 'date'
                            ? renderProgressCell(params.value, maxValues.ad_cost_with_cb, currency, locale, ColorSchema.WARNING, theme)
                            : <CustomLineChart series={series} formatValue={(value) => formatCurrency(value, currency, locale, true, 2)} color={'#ffab00'} type="bar" height={30} width={100} />
                        }
                        <Typography variant="caption" sx={{ width: 80, textAlign: "right" }}>
                            {formatCurrency(params.value, currency, locale, true, 1)}
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        ...(split !== 'date' ? [{
            field: 'ad_cost_with_cb_diff',
            headerName: 'Ad Cost with Primelis Signal Difference',
            minWidth: 260,
            flex: 1,
            type: 'number',
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>Ad Cost with Primelis Signal</span>
                    Difference
                </div>
            ),
            renderCell: (params: any) => {
                const value = Math.round((params.row.ad_cost_with_cb ?? 0) - (params.row.ad_cost_with_cb_compare ?? 0));
                const base = params.row.ad_cost_with_cb_compare ?? 0;
                const percent = base !== 0 ? (value / base) * 100 : 0;
                return (
                    <DatagridDiffCell
                        value={value}
                        compareChip={percent}
                        unit="currency"
                        colors={{ dark: '#b7b7b7', light: '#B7B7B7' }}
                        invertedColor
                    />
                );
            }
        }] : []),
        {
            field: 'ad_cost_without_cb',
            minWidth: isCompare ? 260 : 100,
            flex: 1,
            headerName: "Ad Cost witout Primelis Signal",
            cellClassName: 'sparkline-cell',
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>Ad Cost without Primelis Signal</span>
                </div>
            ),
            renderCell: (params: any) => {
                const series = [{
                    name: 'Ad Cost w/ CB',
                    data: params.row.sparkline_ad_cost_without_cb?.map((point: any) => ({
                        x: new Date(point.date).getTime(),
                        y: point.value
                    })) || []
                }];
                return (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, width: 1, height: 1 }}>
                        {split === 'date'
                            ? renderProgressCell(params.value, maxValues.ad_cost_without_cb, currency, locale, ColorSchema.ERROR, theme)
                            : <CustomLineChart series={series} formatValue={(value) => formatCurrency(value, currency, locale, true, 2)} color={'#b71d18'} type="bar" height={30} width={100} />
                        }
                        <Typography variant="caption" sx={{ width: 80, textAlign: "right" }}>
                            {formatCurrency(params.value, currency, locale, true, 1)}
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        ...(split !== 'date' ? [{
            field: 'ad_cost_without_cb_diff',
            headerName: 'Ad Cost without Primelis Signal Difference',
            minWidth: 260,
            flex: 1,
            type: 'number',
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>Ad Cost without Primelis Signal</span>
                    Difference
                </div>
            ),
            renderCell: (params: any) => {
                const value = Math.round((params.row.ad_cost_without_cb ?? 0) - (params.row.ad_cost_without_cb_compare ?? 0));
                const base = params.row.ad_cost_without_cb_compare ?? 0;
                const percent = base !== 0 ? (value / base) * 100 : 0;
                return (
                    <DatagridDiffCell
                        value={value}
                        compareChip={percent}
                        unit="currency"
                        colors={{ dark: '#b7b7b7', light: '#B7B7B7' }}
                        invertedColor
                    />
                );
            }
        }] : []),
        {
            field: 'brand_traffic',
            headerName: t('dashboard.brandTraffic.totalBrandTraffic'),
            minWidth: 190,
            flex: 1,
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>{t('dashboard.brandTraffic.totalBrandTraffic')}</span>
                </div>
            ),
            renderCell: (params: any) => {
                const series = [{
                    name: t('dashboard.brandTraffic.totalBrandTraffic'),
                    data: params.row.sparkline_brand_traffic?.map((point: any) => ({
                        x: new Date(point.date).getTime(),
                        y: point.value
                    })) || []
                }];
                return (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, width: 1, height: 1 }}>
                        {split === 'date'
                            ? renderProgressCell(params.value, maxValues.brand_traffic, currency, locale, ColorSchema.SECONDARY, theme)
                            : <CustomLineChart series={series} formatValue={(value) => formatNumber(value)} color={'#880e4f'} type="line" height={30} width={100} />
                        }
                        <Typography variant="caption" sx={{ width: 80, textAlign: "right" }}>
                            {formatNumber(params.value)}
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        ...(split !== 'date' ? [{
            field: 'brand_traffic_diff',
            headerName: t('dashboard.brandTraffic.totalBrandTraffic') + " Difference",
            minWidth: 170,
            flex: 1,
            type: 'number',
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>{t('dashboard.brandTraffic.totalBrandTraffic')}</span>
                    Difference
                </div>
            ),
            renderCell: (params: any) => {
                const value = Math.round((params.row.brand_traffic ?? 0) - (params.row.brand_traffic_compare ?? 0));
                const base = params.row.brand_traffic_compare ?? 0;
                const percent = base !== 0 ? (value / base) * 100 : 0;
                return (
                    <DatagridDiffCell
                        value={formatNumber(value)}
                        compareChip={percent}
                        colors={{ dark: '#b7b7b7', light: '#B7B7B7' }}
                    />
                );
            }
        }] : []),
        {
            field: 'blended_ctr',
            headerName: 'Blended CTR',
            minWidth: isCompare ? 190 : 140,
            flex: 1,
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>Blended CTR</span>
                </div>
            ),
            renderCell: (params: any) => {
                const value = params.value ?? 0;
                const series = [{
                    name: 'Blended CTR',
                    data: params.row.sparkline_blended_ctr?.map((point: any) => ({
                        x: new Date(point.date).getTime(),
                        y: point.value
                    })) || []
                }];
                return (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, width: 1, height: 1 }}>
                        {split === 'date'
                            ? renderProgressCell(value, maxValues.blended_ctr, currency, locale, ColorSchema.INFO, theme)
                            : <CustomLineChart color={'#5119b7'} series={series} formatValue={(val) => `${(val).toFixed(1)}%`} type="line" height={30} width={100} />
                        }
                        <Typography variant="caption" sx={{ width: 80, textAlign: "right" }}>
                            {((value).toFixed(1))}%
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        ...(split !== 'date' ? [{
            field: 'blended_ctr_diff',
            headerName: 'Blended CTR Difference',
            minWidth: 180,
            flex: 1,
            type: 'number',
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>Blended CTR</span>
                    Difference
                </div>
            ),
            renderCell: (params: any) => {
                const value = ((params.row.blended_ctr ?? 0) - (params.row.blended_ctr_compare ?? 0));
                const base = (params.row.blended_ctr_compare ?? 0);
                const percent = base !== 0 ? (value / base) * 100 : 0;
                return (
                    <DatagridDiffCell
                        value={Math.round(value)}
                        compareChip={percent}
                        unit="pp"
                        colors={{ dark: '#b7b7b7', light: '#B7B7B7' }}
                    />
                );
            }
        }] : []),
        {
            field: 'revenue_or_conversions',
            headerName: workspace?.template === 'Lead'
                ? t('dashboard.brandOverview.conversions')
                : t('dashboard.revenue'),
            minWidth: isCompare ? 100 : 100,
            flex: 1,
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>
                        {workspace?.template === 'Lead'
                            ? t('dashboard.brandOverview.conversions')
                            : t('dashboard.revenue')}
                    </span>
                </div>
            ),
            renderCell: (params: any) => {
                const value = params.value ?? 0;
                const series = [{
                    name: workspace?.template === 'Lead'
                        ? t('dashboard.brandOverview.conversions')
                        : t('dashboard.revenue'),
                    data: params.row.sparkline_revenue_or_conversions?.map((point: any) => ({
                        x: new Date(point.date).getTime(),
                        y: point.value
                    })) || []
                }];
                return (
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, width: 1, height: 1 }}>
                        {split === 'date'
                            ? renderProgressCell(value, maxValues.revenue_or_conversions, currency, locale, ColorSchema.TERTIARY, theme)
                            : <CustomLineChart series={series} formatValue={(val) => workspace?.template === 'Lead' ? formatNumber(val) : formatCurrency(val, currency, locale, true, 2)} color={'#2A6FD6'} type="bar" height={30} width={100} />
                        }
                        <Typography variant="caption" sx={{ width: 80, textAlign: "right" }}>
                            {workspace?.template === 'Lead'
                                ? formatNumber(value)
                                : formatCurrency(value, currency, locale, true, 1)}
                        </Typography>
                    </Stack>
                );
            },
            type: 'number',
        },
        ...(split !== 'date' ? [{
            field: 'revenue_or_conversions_diff',
            headerName: workspace?.template === 'Lead'
                ? `${t('dashboard.brandOverview.conversions')} Difference`
                : `${t('dashboard.revenue')} Difference`,
            minWidth: 170,
            flex: 1,
            type: 'number',
            renderHeader: () => (
                <div style={{ whiteSpace: 'pre-line', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{ textAlign: 'right' }}>
                        {workspace?.template === 'Lead'
                            ? t('dashboard.brandOverview.conversions')
                            : t('dashboard.revenue')}
                    </span>
                    Difference
                </div>
            ),
            renderCell: (params: any) => {
                const value = Math.round((params.row.revenue_or_conversions ?? 0) - (params.row.revenue_or_conversions_compare ?? 0));
                const base = params.row.revenue_or_conversions_compare ?? 0;
                const percent = base !== 0 ? (value / base) * 100 : 0;
                return (
                    <DatagridDiffCell
                        value={value}
                        compareChip={percent}
                        unit={workspace?.template === 'Lead' ? '' : 'currency'}
                        colors={{ dark: '#b7b7b7', light: '#B7B7B7' }}
                    />
                );
            }
        }] : []),
    ];
};

export default function DataGridPerformanceTable({ data, split, compare, sinceCrossBrand }: any) {
    const filterStore = useSelector((state: any) => state.filters);
    const { t, currentLang } = useLocales();
    const locale = currentLang.value;
    const workspace = useCurrentWorkspace();
    const currency = workspace?.currency || 'USD';
    const theme = useTheme();
    const filteredRows = data.filter((row: any) => row.split !== null);
    const isCompare = !!filterStore?.compare;

    const maxValues = {
        savings: getMaxValue(filteredRows, 'savings'),
        ad_cost_with_cb: getMaxValue(filteredRows, 'ad_cost_with_cb'),
        ad_cost_without_cb: getMaxValue(filteredRows, 'ad_cost_without_cb'),
        revenue_or_conversions: getMaxValue(filteredRows, 'revenue_or_conversions'),
        blended_ctr: getMaxValue(filteredRows, 'blended_ctr'),
        brand_traffic: getMaxValue(filteredRows, 'brand_traffic')
    };

    const columns: any = getColumns({
        t,
        currentLang,
        workspace,
        theme,
        filterStore,
        split,
        isCompare,
        currency,
        locale,
        maxValues,
        sinceCrossBrand
    });

    const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        savings_diff: false,
        ad_cost_with_cb: false,
        ad_cost_without_cb: false,

        ad_cost_with_cb_diff: false,
        ad_cost_without_cb_diff: false,
        revenue_or_conversions_diff: true,
        blended_ctr_diff: true,
    });

    return (
        <Box sx={{
            position: 'relative',
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                outline: 'none !important',
            },
            '& .MuiDataGrid-cell--pinnedLeft': {
                backgroundColor: theme.palette.mode === "dark" ? '#212b36 !important' : 'white !important',
            },
            "& .MuiDataGrid-cell": { overflow: 'visible !important' },
        }}>
            <DataGridPro
                autoHeight
                disableRowSelectionOnClick
                rows={filteredRows.map((r: any, i: any) => ({ id: i, ...r }))}
                pagination
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                paginationModel={paginationModel}
                columns={columns}
                onPaginationModelChange={setPaginationModel}
                disableDensitySelector
                slots={{ toolbar: GridToolbar }}
                initialState={{
                    pinnedColumns: { left: ['split'] }
                }}
            />
        </Box>
    );
}
